import { useEffect, useMemo, useRef, useState } from 'react';

import useCancelRequest from '@intelligence/hooks/useCancelRequest';
import { getTrueDataFeedbackHistoryNew } from '@intelligence/services/topcharts';

import {
  filterAlineaTrueData,
  getAlineaHistoryRecords,
  getAlineaTrueDataSelections,
  shouldRequestAlineaTrueData,
} from '../utils/alineaTrueData';

import type { IMetricsOptionItem } from '../components/MetricsSelection/config';
import type {
  IAlineaHistoryRecord,
  IAlineaTrueDataSeries,
  IAlineaHistoryResponse,
} from '../utils/alineaTrueData';

interface IUseAlineaTrueDataParams {
  gameId?: string;
  entityType?: string;
  dateType?: string;
  startDate?: string;
  endDate?: string;
  selectedMetricOptions?: IMetricsOptionItem[];
  visualMetricOptions?: IMetricsOptionItem[];
  /** 当前页面是否为 Global 聚合；Alinea History 只对该口径有效。 */
  isGlobalAggregate?: boolean;
}

interface IUseAlineaTrueDataReturn {
  alineaTrueDataSeries: IAlineaTrueDataSeries[];
  isAlineaTrueDataLoading: boolean;
}

const EMPTY_ROWS: IAlineaHistoryRecord[] = [];

/**
 * KPI Trends 只在 Global 日粒度且选中目标指标时探测 History；请求由独立 token 取消，
 * 普通趋势接口仍由 useKPITrends 自己管理，避免两条数据链路互相改写参数。
 */
export default function useAlineaTrueData(
  params: IUseAlineaTrueDataParams,
): IUseAlineaTrueDataReturn {
  const {
    gameId,
    entityType,
    dateType,
    startDate,
    endDate,
    selectedMetricOptions = [],
    visualMetricOptions = [],
    isGlobalAggregate = false,
  } = params;
  const { next } = useCancelRequest();
  // useCancelRequest 的 next 通过内部 ref 工作；固定首个函数引用，避免每次渲染重复发起 History。
  const nextRef = useRef(next);
  const [historyRows, setHistoryRows] =
    useState<IAlineaHistoryRecord[]>(EMPTY_ROWS);
  const [isLoading, setIsLoading] = useState(false);

  const requestSelections = useMemo(
    () => getAlineaTrueDataSelections(selectedMetricOptions),
    [selectedMetricOptions],
  );
  const displaySelections = useMemo(
    () => getAlineaTrueDataSelections(visualMetricOptions),
    [visualMetricOptions],
  );

  /**
   * true 表示当前口径既是 Global 聚合，又满足 combine、日粒度、目标指标和游戏边界；
   * 非 Global 视图无法合法叠加 Global History，因此直接取消请求而不是请求后丢弃。
   */
  const shouldRequest =
    isGlobalAggregate &&
    shouldRequestAlineaTrueData({
      gameId,
      entityType,
      dateType,
      selections: requestSelections,
    });

  useEffect(() => {
    // 非 Global、非日粒度、切换游戏或没有目标指标时，立即清掉上一轮真数，避免旧线残留。
    if (!shouldRequest) {
      // 即使当前不需要请求，也要推进 token 版本，取消上一轮正在进行的 History。
      nextRef.current();
      setHistoryRows(EMPTY_ROWS);
      setIsLoading(false);
      return undefined;
    }

    const requestToken = nextRef.current();
    let isActive = true;

    // 切换游戏或重新进入符合条件的粒度时先清空旧行，避免请求期间短暂串线。
    setHistoryRows(EMPTY_ROWS);
    setIsLoading(true);

    getTrueDataFeedbackHistoryNew(
      {
        mode: 'alinea',
        entity_type: 'combine',
        game_id: gameId,
        page_id: '',
      },
      { cancelToken: requestToken.cancelToken },
    )
      .then((response: IAlineaHistoryResponse) => {
        // 取消请求或参数已变化时禁止旧响应回写当前页面。
        if (!isActive || requestToken.isStale()) return;

        const rows = getAlineaHistoryRecords(response);
        if (rows === null) {
          console.error(
            '[KPITrends][AlineaTrueData] History response is invalid or failed.',
          );
          setHistoryRows(EMPTY_ROWS);
          return;
        }
        setHistoryRows(rows);
      })
      .catch((error: unknown) => {
        if (!isActive || requestToken.isStale()) return;
        console.error(
          '[KPITrends][AlineaTrueData] History request failed.',
          error,
        );
        setHistoryRows(EMPTY_ROWS);
      })
      .finally(() => {
        if (isActive && !requestToken.isStale()) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [entityType, gameId, shouldRequest]);

  const series = useMemo(
    () =>
      filterAlineaTrueData({
        rows: historyRows,
        selections: displaySelections,
        startDate,
        endDate,
      }),
    [historyRows, displaySelections, startDate, endDate],
  );

  return {
    alineaTrueDataSeries: series,
    isAlineaTrueDataLoading: isLoading,
  };
}
